import React, { useState } from 'react';
import { GripIcon, PlusCircleIcon, XIcon, EyeIcon, AlertCircleIcon, BrainIcon, CheckCircleIcon, InfoIcon, CalendarIcon, FileTextIcon, MailIcon, PhoneIcon, ListIcon, CheckSquareIcon, ToggleRightIcon, FileIcon, ArrowRightIcon, Settings2Icon, FilterIcon, ZapIcon } from 'lucide-react';
interface Step4EnrollmentFormProps {
  formData: any;
  updateFormData: (data: any) => void;
}
interface FormField {
  id: string;
  type: string;
  label: string;
  required: boolean;
  options?: string[];
  placeholder?: string;
  eligibilityEnabled?: boolean;
  eligibilityRules?: {
    condition: string;
    values: string[];
  };
}
const Step4EnrollmentForm: React.FC<Step4EnrollmentFormProps> = ({
  formData,
  updateFormData
}) => {
  // Default form fields based on program type
  const defaultFields: FormField[] = [{
    id: 'fullName',
    type: 'text',
    label: 'Full Name',
    required: true,
    placeholder: 'Enter your full name'
  }, {
    id: 'email',
    type: 'email',
    label: 'Email Address',
    required: true,
    placeholder: 'Enter your email address'
  }, {
    id: 'phone',
    type: 'tel',
    label: 'Phone Number',
    required: true,
    placeholder: 'Enter your phone number'
  }, {
    id: 'nationality',
    type: 'select',
    label: 'Nationality',
    required: true,
    options: ['UAE', 'Saudi Arabia', 'Qatar', 'Kuwait', 'Bahrain', 'Oman', 'Other']
  }, {
    id: 'education',
    type: 'select',
    label: 'Highest Education Level',
    required: true,
    options: ['High School', "Bachelor's Degree", "Master's Degree", 'Doctorate', 'Other']
  }];
  const [fields, setFields] = useState<FormField[]>(formData.formFields?.length ? formData.formFields : defaultFields);
  const [showAiSuggestions, setShowAiSuggestions] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [editingEligibility, setEditingEligibility] = useState<string | null>(null);
  const [testingEligibility, setTestingEligibility] = useState(false);
  const [testResponses, setTestResponses] = useState<Record<string, any>>({});
  const fieldTypes = [{
    value: 'text',
    label: 'Text Field',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-484" />,
    tooltip: 'Use for short text responses like names or addresses',
    eligibilityCompatible: false
  }, {
    value: 'email',
    label: 'Email Field',
    icon: <MailIcon className="w-4 h-4" data-id="element-485" />,
    tooltip: 'Validates email format automatically',
    eligibilityCompatible: false
  }, {
    value: 'tel',
    label: 'Phone Field',
    icon: <PhoneIcon className="w-4 h-4" data-id="element-486" />,
    tooltip: 'Optimized for phone number input',
    eligibilityCompatible: false
  }, {
    value: 'select',
    label: 'Dropdown',
    icon: <ListIcon className="w-4 h-4" data-id="element-487" />,
    tooltip: 'Use for lists with 5+ options. Great for compact mobile layouts',
    eligibilityCompatible: true
  }, {
    value: 'checkbox',
    label: 'Checkboxes',
    icon: <CheckSquareIcon className="w-4 h-4" data-id="element-488" />,
    tooltip: 'Use when multiple selections are allowed',
    eligibilityCompatible: true
  }, {
    value: 'radio',
    label: 'Radio Buttons',
    icon: <ToggleRightIcon className="w-4 h-4" data-id="element-489" />,
    tooltip: 'Use when only one selection is allowed from a list',
    eligibilityCompatible: true
  }, {
    value: 'file',
    label: 'File Upload',
    icon: <FileIcon className="w-4 h-4" data-id="element-490" />,
    tooltip: 'Allow students to upload documents or certificates',
    eligibilityCompatible: false
  }, {
    value: 'date',
    label: 'Date Picker',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-491" />,
    tooltip: 'Optimized for selecting dates',
    eligibilityCompatible: true
  }, {
    value: 'textarea',
    label: 'Text Area',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-492" />,
    tooltip: 'Use for longer text responses like personal statements',
    eligibilityCompatible: false
  }];
  const aiSuggestions = [{
    id: 'language_certificate',
    title: 'Language Proficiency Certificate',
    description: 'Students in similar programs often require a language certificate (IELTS, TOEFL)',
    field: {
      id: `field_${Date.now()}_language`,
      type: 'file',
      label: 'Language Proficiency Certificate',
      required: true,
      placeholder: 'Upload your IELTS, TOEFL, or other language certificate'
    }
  }, {
    id: 'work_experience',
    title: 'Work Experience',
    description: 'For graduate programs, work experience is often a key eligibility factor',
    field: {
      id: `field_${Date.now()}_experience`,
      type: 'select',
      label: 'Years of Work Experience',
      required: true,
      options: ['None', '1-2 years', '3-5 years', '5+ years'],
      eligibilityEnabled: true,
      eligibilityRules: {
        condition: 'equals',
        values: ['3-5 years', '5+ years']
      }
    }
  }];
  const handleAddField = () => {
    const newField: FormField = {
      id: `field_${Date.now()}`,
      type: 'text',
      label: 'New Field',
      required: false,
      placeholder: 'Enter value'
    };
    const updatedFields = [...fields, newField];
    setFields(updatedFields);
    updateFormData({
      formFields: updatedFields
    });
  };
  const handleAddAiSuggestion = (suggestion: any) => {
    const updatedFields = [...fields, suggestion.field];
    setFields(updatedFields);
    updateFormData({
      formFields: updatedFields
    });
    // Remove the suggestion from visible suggestions
    setShowAiSuggestions(false);
  };
  const handleRemoveField = (id: string) => {
    const updatedFields = fields.filter(field => field.id !== id);
    setFields(updatedFields);
    updateFormData({
      formFields: updatedFields
    });
  };
  const handleFieldChange = (id: string, property: keyof FormField, value: any) => {
    const updatedFields = fields.map(field => {
      if (field.id === id) {
        return {
          ...field,
          [property]: value
        };
      }
      return field;
    });
    setFields(updatedFields);
    updateFormData({
      formFields: updatedFields
    });
  };
  const toggleEligibility = (id: string, enabled: boolean) => {
    const updatedFields = fields.map(field => {
      if (field.id === id) {
        return {
          ...field,
          eligibilityEnabled: enabled,
          eligibilityRules: enabled ? field.eligibilityRules || {
            condition: 'equals',
            values: field.type === 'select' || field.type === 'radio' ? [field.options?.[0] || ''] : []
          } : undefined
        };
      }
      return field;
    });
    setFields(updatedFields);
    updateFormData({
      formFields: updatedFields
    });
    if (enabled) {
      setEditingEligibility(id);
    } else {
      setEditingEligibility(null);
    }
  };
  const updateEligibilityRule = (id: string, property: keyof FormField['eligibilityRules'], value: any) => {
    const updatedFields = fields.map(field => {
      if (field.id === id && field.eligibilityEnabled) {
        return {
          ...field,
          eligibilityRules: {
            ...(field.eligibilityRules || {
              condition: 'equals',
              values: []
            }),
            [property]: value
          }
        };
      }
      return field;
    });
    setFields(updatedFields);
    updateFormData({
      formFields: updatedFields
    });
  };
  const isFieldEligible = (field: FormField) => {
    if (!field.eligibilityEnabled || !field.eligibilityRules) return true;
    const response = testResponses[field.id];
    if (!response) return true;
    const {
      condition,
      values
    } = field.eligibilityRules;
    if (field.type === 'select' || field.type === 'radio') {
      if (condition === 'equals') {
        return values.includes(response);
      }
    } else if (field.type === 'checkbox') {
      // For checkboxes, check if any selected option is in the eligible values
      return response.some((selected: string) => values.includes(selected));
    } else if (field.type === 'date') {
      // Simple date comparison (could be enhanced for more complex rules)
      const responseDate = new Date(response);
      if (condition === 'after') {
        const targetDate = new Date(values[0]);
        return responseDate > targetDate;
      } else if (condition === 'before') {
        const targetDate = new Date(values[0]);
        return responseDate < targetDate;
      }
    }
    return true;
  };
  const getFieldTypeIcon = (type: string) => {
    const fieldType = fieldTypes.find(ft => ft.value === type);
    return fieldType?.icon || <FileTextIcon className="w-4 h-4" data-id="element-493" />;
  };
  const isEligibilityCompatible = (type: string) => {
    const fieldType = fieldTypes.find(ft => ft.value === type);
    return fieldType?.eligibilityCompatible || false;
  };
  // Render form field based on type
  const renderFormField = (field: FormField) => {
    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
        return <input type={field.type} placeholder={field.placeholder} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" disabled={!testingEligibility && showPreview} value={testingEligibility ? testResponses[field.id] || '' : ''} onChange={e => setTestResponses({
          ...testResponses,
          [field.id]: e.target.value
        })} data-id="element-494" />;
      case 'textarea':
        return <textarea placeholder={field.placeholder} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" disabled={!testingEligibility && showPreview} value={testingEligibility ? testResponses[field.id] || '' : ''} onChange={e => setTestResponses({
          ...testResponses,
          [field.id]: e.target.value
        })} data-id="element-495" />;
      case 'select':
        return <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" disabled={!testingEligibility && showPreview} value={testingEligibility ? testResponses[field.id] || '' : ''} onChange={e => setTestResponses({
          ...testResponses,
          [field.id]: e.target.value
        })} data-id="element-496">
            <option value="" data-id="element-497">Select an option</option>
            {field.options?.map((option, index) => <option key={index} value={option} data-id="element-498">
                {option}
              </option>)}
          </select>;
      case 'file':
        return <input type="file" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" disabled={!testingEligibility && showPreview} onChange={e => setTestResponses({
          ...testResponses,
          [field.id]: e.target.files?.[0]?.name || 'file.pdf'
        })} data-id="element-499" />;
      case 'checkbox':
        return <div className="space-y-2" data-id="element-500">
            {field.options?.map((option, index) => <div key={index} className="flex items-center" data-id="element-501">
                <input type="checkbox" name={field.id} value={option} className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-gray-300 rounded" disabled={!testingEligibility && showPreview} checked={testingEligibility ? (testResponses[field.id] || []).includes(option) : false} onChange={e => {
              const currentValues = testResponses[field.id] || [];
              const newValues = e.target.checked ? [...currentValues, option] : currentValues.filter((v: string) => v !== option);
              setTestResponses({
                ...testResponses,
                [field.id]: newValues
              });
            }} data-id="element-502" />
                <span className="ml-2 text-sm text-gray-600" data-id="element-503">{option}</span>
              </div>)}
          </div>;
      case 'radio':
        return <div className="space-y-2" data-id="element-504">
            {field.options?.map((option, index) => <div key={index} className="flex items-center" data-id="element-505">
                <input type="radio" name={field.id} value={option} className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-gray-300" disabled={!testingEligibility && showPreview} checked={testingEligibility ? testResponses[field.id] === option : false} onChange={e => setTestResponses({
              ...testResponses,
              [field.id]: option
            })} data-id="element-506" />
                <span className="ml-2 text-sm text-gray-600" data-id="element-507">{option}</span>
              </div>)}
          </div>;
      case 'date':
        return <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" disabled={!testingEligibility && showPreview} value={testingEligibility ? testResponses[field.id] || '' : ''} onChange={e => setTestResponses({
          ...testResponses,
          [field.id]: e.target.value
        })} data-id="element-508" />;
      default:
        return null;
    }
  };
  const renderEligibilityEditor = (field: FormField) => {
    if (!field.eligibilityEnabled) return null;
    switch (field.type) {
      case 'select':
      case 'radio':
        return <div className="mt-4 p-4 bg-gray-50 rounded-md border border-gray-200" data-id="element-509">
            <div className="mb-3" data-id="element-510">
              <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-511">
                Eligible if answer is:
              </label>
              <div className="space-y-2 ml-2" data-id="element-512">
                {field.options?.map((option, index) => <div key={index} className="flex items-center" data-id="element-513">
                    <input type="checkbox" id={`eligibility-${field.id}-${index}`} checked={field.eligibilityRules?.values.includes(option) || false} onChange={e => {
                  const currentValues = field.eligibilityRules?.values || [];
                  const newValues = e.target.checked ? [...currentValues, option] : currentValues.filter(v => v !== option);
                  updateEligibilityRule(field.id, 'values', newValues);
                }} className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-gray-300 rounded" data-id="element-514" />
                    <label htmlFor={`eligibility-${field.id}-${index}`} className="ml-2 text-sm text-gray-700" data-id="element-515">
                      {option}
                    </label>
                  </div>)}
              </div>
            </div>
            <div className="text-xs text-gray-500 flex items-center mt-2 bg-white p-2 rounded border border-gray-200" data-id="element-516">
              <AlertCircleIcon className="w-4 h-4 mr-2 text-primary-500" data-id="element-517" />
              Applicants will only be eligible if they select one of the checked
              options.
            </div>
          </div>;
      case 'checkbox':
        return <div className="mt-4 p-4 bg-gray-50 rounded-md border border-gray-200" data-id="element-518">
            <div className="mb-3" data-id="element-519">
              <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-520">
                Eligible if any of these are selected:
              </label>
              <div className="space-y-2 ml-2" data-id="element-521">
                {field.options?.map((option, index) => <div key={index} className="flex items-center" data-id="element-522">
                    <input type="checkbox" id={`eligibility-${field.id}-${index}`} checked={field.eligibilityRules?.values.includes(option) || false} onChange={e => {
                  const currentValues = field.eligibilityRules?.values || [];
                  const newValues = e.target.checked ? [...currentValues, option] : currentValues.filter(v => v !== option);
                  updateEligibilityRule(field.id, 'values', newValues);
                }} className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-gray-300 rounded" data-id="element-523" />
                    <label htmlFor={`eligibility-${field.id}-${index}`} className="ml-2 text-sm text-gray-700" data-id="element-524">
                      {option}
                    </label>
                  </div>)}
              </div>
            </div>
            <div className="text-xs text-gray-500 flex items-center mt-2 bg-white p-2 rounded border border-gray-200" data-id="element-525">
              <AlertCircleIcon className="w-4 h-4 mr-2 text-primary-500" data-id="element-526" />
              Applicants will be eligible if they select at least one of the
              checked options.
            </div>
          </div>;
      case 'date':
        return <div className="mt-4 p-4 bg-gray-50 rounded-md border border-gray-200" data-id="element-527">
            <div className="mb-3" data-id="element-528">
              <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-529">
                Eligible if date is:
              </label>
              <div className="grid grid-cols-2 gap-3" data-id="element-530">
                <select value={field.eligibilityRules?.condition || 'after'} onChange={e => updateEligibilityRule(field.id, 'condition', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-531">
                  <option value="after" data-id="element-532">After</option>
                  <option value="before" data-id="element-533">Before</option>
                </select>
                <input type="date" value={field.eligibilityRules?.values[0] || ''} onChange={e => {
                updateEligibilityRule(field.id, 'values', [e.target.value]);
              }} className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-534" />
              </div>
            </div>
            <div className="text-xs text-gray-500 flex items-center mt-2 bg-white p-2 rounded border border-gray-200" data-id="element-535">
              <AlertCircleIcon className="w-4 h-4 mr-2 text-primary-500" data-id="element-536" />
              Applicants will only be eligible if they select a date that meets
              this condition.
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div data-id="element-537">
      <h2 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-538">
        Enrollment Form Builder
      </h2>
      <p className="text-gray-600 mb-6" data-id="element-539">
        Customize the application form students will complete to apply to this
        program.
      </p>
      {/* AI Suggestions */}
      {showAiSuggestions && <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6" data-id="element-540">
          <div className="flex items-start" data-id="element-541">
            <div className="flex-shrink-0 bg-blue-100 rounded-full p-2 mr-3" data-id="element-542">
              <BrainIcon className="w-5 h-5 text-blue-600" data-id="element-543" />
            </div>
            <div className="flex-1" data-id="element-544">
              <h3 className="font-medium text-blue-800 mb-1" data-id="element-545">
                AI Form Field Suggestions
              </h3>
              <p className="text-sm text-blue-700 mb-4" data-id="element-546">
                Based on similar programs, we recommend adding these fields to
                your application form:
              </p>
              <div className="space-y-3" data-id="element-547">
                {aiSuggestions.map(suggestion => <div key={suggestion.id} className="bg-white border border-blue-200 rounded-md p-3 flex justify-between items-center" data-id="element-548">
                    <div data-id="element-549">
                      <h4 className="text-sm font-medium text-gray-800" data-id="element-550">
                        {suggestion.title}
                      </h4>
                      <p className="text-xs text-gray-500" data-id="element-551">
                        {suggestion.description}
                      </p>
                    </div>
                    <button onClick={() => handleAddAiSuggestion(suggestion)} className="text-blue-600 hover:bg-blue-50 rounded-md px-3 py-1 text-sm font-medium flex items-center" data-id="element-552">
                      <PlusCircleIcon className="w-4 h-4 mr-1" data-id="element-553" />
                      Add Field
                    </button>
                  </div>)}
              </div>
              <div className="mt-3 flex justify-end" data-id="element-554">
                <button onClick={() => setShowAiSuggestions(false)} className="text-blue-600 hover:text-blue-800 text-sm" data-id="element-555">
                  Dismiss suggestions
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Form Builder Controls */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-6" data-id="element-556">
        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200 bg-gray-50" data-id="element-557">
          <div className="flex items-center" data-id="element-558">
            <FileTextIcon className="w-5 h-5 text-gray-500 mr-2" data-id="element-559" />
            <h3 className="text-md font-medium text-gray-800" data-id="element-560">Form Fields</h3>
          </div>
          <div className="flex space-x-2" data-id="element-561">
            {testingEligibility ? <button type="button" onClick={() => setTestingEligibility(false)} className="px-3 py-1.5 bg-primary-500 text-white text-sm rounded-md flex items-center" data-id="element-562">
                <CheckCircleIcon className="w-4 h-4 mr-1.5" data-id="element-563" />
                Exit Test Mode
              </button> : <>
                <button type="button" onClick={() => {
              setTestingEligibility(true);
              setShowPreview(true);
              setTestResponses({});
            }} className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-sm rounded-md flex items-center hover:bg-gray-50" disabled={showPreview} data-id="element-564">
                  <FilterIcon className="w-4 h-4 mr-1.5" data-id="element-565" />
                  Test Eligibility
                </button>
                <button type="button" onClick={() => setShowPreview(!showPreview)} className={`px-3 py-1.5 ${showPreview ? 'bg-primary-500 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'} text-sm rounded-md flex items-center`} data-id="element-566">
                  <EyeIcon className="w-4 h-4 mr-1.5" data-id="element-567" />
                  {showPreview ? 'Exit Preview' : 'Preview Form'}
                </button>
              </>}
            <button type="button" onClick={handleAddField} className="px-3 py-1.5 bg-primary-500 text-white text-sm rounded-md flex items-center hover:bg-primary-600" disabled={showPreview && !testingEligibility} data-id="element-568">
              <PlusCircleIcon className="w-4 h-4 mr-1.5" data-id="element-569" />
              Add Field
            </button>
          </div>
        </div>
        {/* Preview mode notice */}
        {showPreview && !testingEligibility && <div className="bg-yellow-50 border-b border-yellow-100 px-5 py-3" data-id="element-570">
            <div className="flex items-center text-sm text-yellow-800" data-id="element-571">
              <AlertCircleIcon className="w-4 h-4 mr-2 text-yellow-600" data-id="element-572" />
              You are in preview mode. This is how students will see the form.
            </div>
          </div>}
        {/* Test eligibility mode notice */}
        {testingEligibility && <div className="bg-blue-50 border-b border-blue-100 px-5 py-3" data-id="element-573">
            <div className="flex items-center text-sm text-blue-800" data-id="element-574">
              <ZapIcon className="w-4 h-4 mr-2 text-blue-600" data-id="element-575" />
              Test eligibility mode. Fill out the form to see which fields would
              be shown or hidden based on eligibility rules.
            </div>
          </div>}
        <div className="p-5" data-id="element-576">
          <div className="space-y-4" data-id="element-577">
            {fields.map((field, index) => {
            // In test mode, check if the field should be shown based on eligibility
            const isEligible = testingEligibility ? isFieldEligible(field) : true;
            return <div key={field.id} className={`rounded-lg border ${editingEligibility === field.id ? 'border-primary-500 ring-1 ring-primary-500' : testingEligibility && !isEligible ? 'border-red-200 bg-red-50' : 'border-gray-200 hover:border-gray-300'} ${showPreview ? 'bg-white' : 'bg-white'} transition-all duration-150 overflow-hidden`} data-id="element-578">
                  {!showPreview || testingEligibility ? <div className="p-4" data-id="element-579">
                      <div className="flex justify-between items-center mb-3" data-id="element-580">
                        <div className="flex items-center" data-id="element-581">
                          <GripIcon className="w-5 h-5 text-gray-400 mr-2 cursor-move" data-id="element-582" />
                          <div className="flex items-center" data-id="element-583">
                            <div className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary-50 text-primary-500 text-sm font-medium mr-2" data-id="element-584">
                              {index + 1}
                            </div>
                            <div className="flex items-center" data-id="element-585">
                              <span className="text-sm font-medium text-gray-700 mr-2" data-id="element-586">
                                {getFieldTypeIcon(field.type)}
                              </span>
                              <span className="text-sm font-medium text-gray-700" data-id="element-587">
                                Field #{index + 1}
                              </span>
                              {field.eligibilityEnabled && <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full flex items-center" data-id="element-588">
                                  <FilterIcon className="w-3 h-3 mr-1" data-id="element-589" />
                                  Eligibility Filter
                                </span>}
                            </div>
                          </div>
                        </div>
                        <button type="button" onClick={() => handleRemoveField(field.id)} className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100" data-id="element-590">
                          <XIcon className="w-5 h-5" data-id="element-591" />
                        </button>
                      </div>
                      {testingEligibility ? <div data-id="element-592">
                          {!isEligible && <div className="mb-3 p-2 bg-red-50 border border-red-100 rounded text-sm text-red-800 flex items-center" data-id="element-593">
                              <AlertCircleIcon className="w-4 h-4 mr-2 text-red-500" data-id="element-594" />
                              This field would be hidden based on eligibility
                              rules
                            </div>}
                          <label className={`block text-sm font-medium ${field.required ? 'text-gray-700' : 'text-gray-600'} mb-1`} data-id="element-595">
                            {field.label}{' '}
                            {field.required && <span className="text-red-500" data-id="element-596">*</span>}
                          </label>
                          {renderFormField(field)}
                        </div> : <div className="space-y-3" data-id="element-597">
                          <div className="grid grid-cols-2 gap-3" data-id="element-598">
                            <div data-id="element-599">
                              <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-600">
                                Field Label
                              </label>
                              <input type="text" value={field.label} onChange={e => handleFieldChange(field.id, 'label', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-601" />
                            </div>
                            <div data-id="element-602">
                              <div className="flex items-center mb-1" data-id="element-603">
                                <label className="block text-xs font-medium text-gray-500" data-id="element-604">
                                  Field Type
                                </label>
                                <div className="relative ml-1 group" data-id="element-605">
                                  <InfoIcon className="w-3.5 h-3.5 text-gray-400 cursor-help" data-id="element-606" />
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10" data-id="element-607">
                                    {fieldTypes.find(ft => ft.value === field.type)?.tooltip}
                                  </div>
                                </div>
                              </div>
                              <select value={field.type} onChange={e => handleFieldChange(field.id, 'type', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-608">
                                {fieldTypes.map(type => <option key={type.value} value={type.value} data-id="element-609">
                                    {type.label}
                                  </option>)}
                              </select>
                            </div>
                          </div>
                          {(field.type === 'text' || field.type === 'email' || field.type === 'tel' || field.type === 'textarea') && <div data-id="element-610">
                              <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-611">
                                Placeholder Text
                              </label>
                              <input type="text" value={field.placeholder || ''} onChange={e => handleFieldChange(field.id, 'placeholder', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-612" />
                            </div>}
                          {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && <div data-id="element-613">
                              <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-614">
                                Options (comma separated)
                              </label>
                              <input type="text" value={field.options?.join(', ') || ''} onChange={e => handleFieldChange(field.id, 'options', e.target.value.split(',').map(opt => opt.trim()))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="Option 1, Option 2, Option 3" data-id="element-615" />
                            </div>}
                          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-1" data-id="element-616">
                            <div className="flex items-center" data-id="element-617">
                              <input type="checkbox" id={`required-${field.id}`} checked={field.required} onChange={e => handleFieldChange(field.id, 'required', e.target.checked)} className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-gray-300 rounded" data-id="element-618" />
                              <label htmlFor={`required-${field.id}`} className="ml-2 text-sm text-gray-700" data-id="element-619">
                                Required field
                              </label>
                            </div>
                            {isEligibilityCompatible(field.type) && <div className="flex items-center" data-id="element-620">
                                <input type="checkbox" id={`eligibility-toggle-${field.id}`} checked={field.eligibilityEnabled || false} onChange={e => toggleEligibility(field.id, e.target.checked)} className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-gray-300 rounded" data-id="element-621" />
                                <div className="flex items-center" data-id="element-622">
                                  <label htmlFor={`eligibility-toggle-${field.id}`} className="ml-2 text-sm text-gray-700" data-id="element-623">
                                    Use for eligibility
                                  </label>
                                  <div className="relative ml-1 group" data-id="element-624">
                                    <InfoIcon className="w-3.5 h-3.5 text-gray-400 cursor-help" data-id="element-625" />
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10" data-id="element-626">
                                      Automatically screen applicants based on
                                      their responses to this question.
                                    </div>
                                  </div>
                                </div>
                              </div>}
                          </div>
                          {/* Eligibility Rules Editor */}
                          {editingEligibility === field.id && renderEligibilityEditor(field)}
                          {field.eligibilityEnabled && editingEligibility !== field.id && <div className="mt-2 flex justify-end" data-id="element-627">
                                <button onClick={() => setEditingEligibility(field.id)} className="text-sm text-primary-500 flex items-center hover:underline" data-id="element-628">
                                  <Settings2Icon className="w-3 h-3 mr-1" data-id="element-629" />
                                  Edit eligibility rules
                                </button>
                              </div>}
                        </div>}
                    </div> : <div className="p-4" data-id="element-630">
                      <label className={`block text-sm font-medium ${field.required ? 'text-gray-700' : 'text-gray-600'} mb-1`} data-id="element-631">
                        {field.label}{' '}
                        {field.required && <span className="text-red-500" data-id="element-632">*</span>}
                      </label>
                      {renderFormField(field)}
                    </div>}
                </div>;
          })}
          </div>
          {fields.length === 0 && <div className="text-center py-8 bg-white rounded-lg border border-dashed border-gray-300" data-id="element-633">
              <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3" data-id="element-634">
                <PlusCircleIcon className="w-6 h-6 text-gray-400" data-id="element-635" />
              </div>
              <h3 className="text-gray-500 font-medium mb-1" data-id="element-636">
                No form fields added
              </h3>
              <p className="text-gray-400 text-sm mb-4" data-id="element-637">
                Start by adding fields to your enrollment form
              </p>
              <button type="button" onClick={handleAddField} className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors" data-id="element-638">
                Add First Field
              </button>
            </div>}
          {showPreview && fields.length > 0 && !testingEligibility && <div className="mt-6 flex justify-center" data-id="element-639">
              <button type="button" className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors" disabled data-id="element-640">
                Submit Application
              </button>
            </div>}
        </div>
      </div>
      {/* Guidance and notes */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6" data-id="element-641">
        <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center" data-id="element-642">
          <InfoIcon className="w-4 h-4 mr-2 text-primary-500" data-id="element-643" />
          Form Builder Tips
        </h3>
        <ul className="space-y-2 text-sm text-gray-600" data-id="element-644">
          <li className="flex items-start" data-id="element-645">
            <ArrowRightIcon className="w-3 h-3 text-primary-500 mt-1 mr-2 flex-shrink-0" data-id="element-646" />
            Use <span className="font-medium" data-id="element-647">eligibility rules</span> to
            automatically screen applicants based on their responses.
          </li>
          <li className="flex items-start" data-id="element-648">
            <ArrowRightIcon className="w-3 h-3 text-primary-500 mt-1 mr-2 flex-shrink-0" data-id="element-649" />
            Test your form in <span className="font-medium" data-id="element-650">preview mode</span>{' '}
            to see how it will appear to applicants.
          </li>
          <li className="flex items-start" data-id="element-651">
            <ArrowRightIcon className="w-3 h-3 text-primary-500 mt-1 mr-2 flex-shrink-0" data-id="element-652" />
            Required fields are marked with an asterisk (
            <span className="text-red-500" data-id="element-653">*</span>) for applicants.
          </li>
        </ul>
      </div>
    </div>;
};
export default Step4EnrollmentForm;